(function () {
  'use strict';

  if (customElements.get('cart-upsell') && customElements.get('cart-bundle-upgrade')) return;

  /**
   * Shared plumbing for the two cart-drawer CRO add-to-cart controls.
   * Reuses Dawn's own primitives: fetchConfig / routes / publish /
   * PUB_SUB_EVENTS, and <cart-drawer>.getSectionsToRender() + renderContents()
   * so the drawer is repainted through the Section Rendering API rather than by
   * hand-rolled DOM patching.
   */

  function requestConfig() {
    if (typeof window.fetchConfig === 'function') return window.fetchConfig();
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    };
  }

  function cartRoute(name, fallback) {
    var routes = window.routes || {};
    return routes[name] || fallback;
  }

  function findDrawer(element) {
    return element.closest('cart-drawer') || document.querySelector('cart-drawer');
  }

  function sectionIdsFor(drawer) {
    if (!drawer || typeof drawer.getSectionsToRender !== 'function') return [];
    return drawer.getSectionsToRender().map(function (section) {
      return section.id;
    });
  }

  // Resolves with the parsed cart response, rejects on any failure shape.
  // The Cart AJAX API reports errors as `{ status, message, description }` or
  // as `{ errors: {...} }` depending on the endpoint, so both are checked.
  function postJSON(url, payload) {
    var config = requestConfig();
    config.body = JSON.stringify(payload);
    return fetch(url, config).then(function (response) {
      return response.json().then(function (body) {
        if (!response.ok || !body || body.status || body.errors) {
          throw new Error('cart-request-failed');
        }
        return body;
      });
    });
  }

  function paintCart(drawer, state) {
    if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-cro-add',
        cartData: state,
      });
    }

    if (drawer && typeof drawer.renderContents === 'function' && state && state.sections) {
      drawer.renderContents(state);
    }
  }

  // The clicked control is destroyed by the section re-render, so park focus on
  // an equivalent element in the fresh markup (or the checkout button).
  function restoreFocus(selector) {
    var target = document.querySelector(selector) || document.getElementById('CartDrawer-Checkout');
    if (target && typeof target.focus === 'function') target.focus();
  }

  function showError(host, message) {
    var errorElement = host.querySelector('[data-cro-error]');
    if (errorElement) errorElement.textContent = message;
  }

  function setBusy(button, isBusy) {
    if (!button) return;
    button.toggleAttribute('disabled', isBusy);
    button.classList.toggle('is-loading', isBusy);
    button.setAttribute('aria-busy', isBusy ? 'true' : 'false');
  }

  if (!customElements.get('cart-upsell')) {
    /**
     * <cart-upsell> — compact "customers also add" rows. Each row's button
     * adds one unit of its variant via /cart/add.js, then repaints the drawer.
     */
    customElements.define(
      'cart-upsell',
      class CartUpsell extends HTMLElement {
        constructor() {
          super();
          this.onClick = this.onClick.bind(this);
        }

        connectedCallback() {
          this.addEventListener('click', this.onClick);
        }

        disconnectedCallback() {
          this.removeEventListener('click', this.onClick);
        }

        onClick(event) {
          var button = event.target.closest('[data-upsell-add]');
          if (!button || !this.contains(button)) return;

          var variantId = button.dataset.variantId;
          if (!variantId) return;

          event.preventDefault();
          showError(this, '');
          setBusy(button, true);

          var host = this;
          var drawer = findDrawer(this);
          var errorText = this.dataset.errorText || '';

          postJSON(cartRoute('cart_add_url', '/cart/add'), {
            items: [{ id: variantId, quantity: 1 }],
            sections: sectionIdsFor(drawer),
            sections_url: window.location.pathname,
          })
            .then(function (state) {
              paintCart(drawer, state);
              restoreFocus('cart-upsell [data-upsell-add]');
            })
            .catch(function () {
              setBusy(button, false);
              showError(host, errorText);
            });
        }
      }
    );
  }

  if (!customElements.get('cart-bundle-upgrade')) {
    /**
     * <cart-bundle-upgrade> — adds the merchant-picked bundle product. When the
     * merchant has opted in (data-replace-keys present), the line items that
     * were in the cart when the button rendered are set to quantity 0 in a
     * second /cart/update.js call, after the bundle has been added.
     */
    customElements.define(
      'cart-bundle-upgrade',
      class CartBundleUpgrade extends HTMLElement {
        constructor() {
          super();
          this.onClick = this.onClick.bind(this);
        }

        connectedCallback() {
          this.button = this.querySelector('[data-bundle-add]');
          if (!this.button) return;
          this.addEventListener('click', this.onClick);
        }

        disconnectedCallback() {
          this.removeEventListener('click', this.onClick);
        }

        replacementUpdates() {
          var raw = this.dataset.replaceKeys || '';
          var updates = {};
          var hasKeys = false;

          raw.split(',').forEach(function (key) {
            var trimmed = key.trim();
            if (!trimmed) return;
            updates[trimmed] = 0;
            hasKeys = true;
          });

          return hasKeys ? updates : null;
        }

        onClick(event) {
          var button = event.target.closest('[data-bundle-add]');
          if (!button || !this.contains(button)) return;

          var variantId = this.dataset.variantId;
          if (!variantId) return;

          event.preventDefault();
          showError(this, '');
          setBusy(button, true);

          var host = this;
          var drawer = findDrawer(this);
          var sections = sectionIdsFor(drawer);
          var updates = this.replacementUpdates();
          var errorText = this.dataset.errorText || '';

          var addPayload = { items: [{ id: variantId, quantity: 1 }] };
          if (!updates) {
            addPayload.sections = sections;
            addPayload.sections_url = window.location.pathname;
          }

          postJSON(cartRoute('cart_add_url', '/cart/add'), addPayload)
            .then(function (state) {
              if (!updates) return state;

              return postJSON(cartRoute('cart_update_url', '/cart/update'), {
                updates: updates,
                sections: sections,
                sections_url: window.location.pathname,
              });
            })
            .then(function (state) {
              paintCart(drawer, state);
              restoreFocus('cart-bundle-upgrade [data-bundle-add]');
            })
            .catch(function () {
              setBusy(button, false);
              showError(host, errorText);
            });
        }
      }
    );
  }
})();
